import { SeLinkBase } from './se-link-base';
import { html, TemplateResult } from 'lit';
import {
  customElement,
  property,
  queryAssignedElements,
  state,
} from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { classMap } from 'lit/directives/class-map.js';
import { AdoptedStyleSheets } from '../../controllers/adopted-stylesheets';
import innerCSS from './se-link.inner.pcss?inline';
import '../outline-icon/outline-icon';
import { whenParsed } from '../../utilities/when-parsed';

export const seLinkVariants = ['cta', 'cta-alt', 'cta-alt-2', 'tag', 'default'];
export type SELinkVariant = typeof seLinkVariants[number];

export const seLinkSizes = ['small', 'medium', 'large'] as const;
export type SELinkSize = typeof seLinkSizes[number];

export const seLinkDirections = ['right', 'left'];
export type SELinkDirection = typeof seLinkDirections[number];

/**
 * The Saul-Ewing  Link component
 * @slot - The default, and only slot for this element.
 */
@customElement('se-link')
export class SeLink extends SeLinkBase {
  static styles = [];

  private stylesheetController = new AdoptedStyleSheets(this, {
    encapsulatedCSS: innerCSS,
  });

  /**
   * Link Variant: 'cta' | 'tag' | 'undefined'
   */
  @property({ type: String })
  variant: SELinkVariant = 'default';

  /**
   * Link Size: 'small'| 'medium | 'large'
   * */
  @property({ type: String })
  size: SELinkSize;

  /**
   * Ref to default slot
   */
  @queryAssignedElements()
  slotted!: Array<HTMLElement>;

  /**
   * Optional/Recommended aria-label text.
   */
  @property({ type: String })
  label: string;

  /**
   * true if slotted content is <a>
   */
  @state() isAnchorSlotted: boolean;

  /**
   * Link Direction: 'right' | 'left' | 'undefined'
   */
  @property({ type: String })
  direction: SELinkDirection = 'right';

  async firstUpdated(): Promise<void> {
    // Reads this.slotted (light-DOM children). When se-link is registered by
    // the critical bundle it upgrades mid-parse, before those children exist --
    // isAnchorSlotted would be false and the CTA arrow never appended.
    // See utilities/when-parsed.ts (SE-1004).
    await whenParsed();

    // returns true if <a> is slotted.
    this.isAnchorSlotted =
      this.slotted.filter(el => el.localName === 'a').length > 0;
    this.appendCtaIconToSlottedContent();
  }

  render(): TemplateResult {
    const wrapperClasses = {
      'default': this.variant === 'default',
      'cta': this.variant === 'cta' && !this.isAnchorSlotted,
      'cta-alt': this.variant === 'cta-alt' && !this.isAnchorSlotted,
      'cta-alt-2': this.variant === 'cta-alt-2' && !this.isAnchorSlotted,
      'cta--left':
        this.variant === 'cta' &&
        this.direction === 'left' &&
        !this.isAnchorSlotted,
      'tag': this.variant === 'tag',
      'cta-slot': this.isAnchorSlotted && this.variant === 'cta',
      'cta-slot-alt': this.isAnchorSlotted && this.variant === 'cta-alt',
      'cta-slot-alt-2': this.isAnchorSlotted && this.variant === 'cta-alt-2',
      'cta-slot--left':
        this.isAnchorSlotted &&
        this.variant === 'cta' &&
        this.direction === 'left',
      [this.size]:
        this.size &&
        (this.variant === 'cta' ||
          this.variant === 'cta-alt' ||
          this.variant === 'cta-alt-2'),
    };

    return html`${this.linkHref
      ? html` <a
          class="${classMap(wrapperClasses)}"
          href=${this.linkHref}
          rel="${ifDefined(this.linkRel)}"
          target="${ifDefined(this.linkTarget)}"
          aria-label="${ifDefined(this.label)}"
        >
          ${this.direction === 'left' ? this.ctaIconTemplate() : null}
          ${this.linkText ? html`${this.linkText}` : html`<slot></slot>`}
          ${this.direction === 'right' &&
          (this.variant === 'cta' ||
            this.variant === 'cta-alt' ||
            this.variant === 'cta-alt-2')
            ? this.ctaIconTemplate()
            : null}
        </a>`
      : html`<slot class="${classMap(wrapperClasses)}"></slot>`}`;
  }

  /**
   * Generates and appends outline-icon to default slot if <a> slotted
   * and variant is cta or cta-alt.
   */
  appendCtaIconToSlottedContent() {
    if (!['cta', 'cta-alt', 'cta-alt-2'].includes(this.variant)) {
      return;
    }
    if (this.isAnchorSlotted) {
      const slot = this.slotted[0].parentElement as HTMLElement;
      const icon = document.createElement('outline-icon');
      icon.setAttribute(
        'name',
        this.direction === 'left' ? 'arrow-left' : 'arrow-right'
      );
      icon.setAttribute('library', 'system');
      icon.setAttribute('size', '1em');
      icon.setAttribute(
        'color',
        ['cta', 'cta-left'].includes(this.variant)
          ? 'brand-primary-2'
          : 'brand-secondary-1'
      );
      if (this.direction === 'left') {
        slot?.prepend(icon);
      } else {
        slot?.appendChild(icon);
      }
    }
  }

  /**
   * returns outline-icon, only called if <a> is not slotted and variant is
   * cta or cta-alt.
   */
  ctaIconTemplate() {
    return html`<outline-icon
      color="${this.variant === 'cta'
        ? 'brand-primary-2'
        : 'brand-secondary-1'}"
      library="system"
      name="${this.direction === 'left' ? 'arrow-left' : 'arrow-right'}"
      size="1em"
    >
    </outline-icon> `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'se-link': SeLink;
  }
}
